import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Callout } from "@/components/ui/feedback";
import { CopyButton } from "@/components/ui/misc";
import { apiUrl } from "@/lib/api";

export interface ShownTriggerSecret {
  triggerId: number;
  name: string;
  path: string;
  header: string;
  secret: string;
  /** A rotation, which retires the secret the sender may still be using. */
  rotated: boolean;
}

/**
 * What a sender needs to call a trigger, the one time all of it can be shown: the URL, the header
 * and the secret (ADR-021). core-api keeps only the secret's hash, so this dialog is the only place
 * it exists outside the sender, and it lives exactly as long as the dialog does.
 */
export function TriggerSecretDialog({
  secret, onClose,
}: {
  secret: ShownTriggerSecret | undefined;
  onClose: () => void;
}) {
  return (
    <Dialog open={Boolean(secret)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{secret?.rotated ? "New secret" : "The trigger's URL and secret"}</DialogTitle>
          <DialogDescription>
            Put these in whatever calls {secret?.name}. The secret is shown this once: a lost one is rotated,
            never read back.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <CopyRow label="URL" value={secret ? apiUrl(secret.path) : ""} />
          <CopyRow label="Header" value={secret?.header ?? ""} />
          <CopyRow label="Secret" value={secret?.secret ?? ""} />
          {secret?.rotated && (
            <Callout tone="warning">
              A request with the old secret is refused from now on. Put the new one in the sender's headers.
            </Callout>
          )}
          <p className="text-xs text-text-muted">
            Every request is a POST to the URL, with the secret as the value of the {secret?.header} header. The
            trigger's page shows a body it reads, and the steps for a zap.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="default" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CopyRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs font-medium text-text">{label}</p>
      <p className="flex items-center justify-between gap-2 rounded-md border border-border bg-surface-sunken px-3 py-2">
        <span className="break-all font-mono text-xs text-text">{value}</span>
        <CopyButton value={value} label={`Copy the ${label.toLowerCase()}`} />
      </p>
    </div>
  );
}
